import SignupCompletionGate from "@/features/auth/SignupCompletionGate";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useUserPreferences } from "../../context/UserPreferencesContext";
import NextSteps from "@/components/navigation/NextSteps";
import { loadSignedOutAppearance } from "@/themes/appearance";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { session, user, isLoading, authReady } = useAuth();
  const { preferences } = useUserPreferences();
  const location = useLocation();

  // E2E bypass: allows Playwright tests to exercise UI flows without Supabase auth.
  // Enabled only when running Vite with `--mode e2e` (see `client/.env.e2e`).
  if (import.meta.env.VITE_E2E_BYPASS_AUTH === "1") {
    return <>{children}</>;
  }

  // Single-domain by design — see hostRouting.ts. Every protected route
  // stays same-origin on novaristudy.site; there is no separate app
  // subdomain to redirect to.

  // Auth resolves concurrently while JS chunks are being loaded by <Suspense>.
  // Returning null here (rather than another full-screen spinner) prevents the
  // double-loading-screen effect where users see two consecutive loaders.
  if (isLoading || !authReady) {
    return null;
  }

  if (!session) {
    const returnTo = location.pathname + location.search + location.hash;
    const loginPath = /^\/(?:profile|settings)\/kids(?:\/|$)/.test(location.pathname) || loadSignedOutAppearance()?.kids ? "/login/kids" : "/login";
    return <Navigate to={`${loginPath}?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  const isOnboardingPage = location.pathname === "/onboarding";
  const onboardingCompleted =
    preferences?.onboarding_completed ?? user?.onboarding_completed ?? false;
  const needsOnboarding = !onboardingCompleted;

  if (needsOnboarding && !isOnboardingPage) {
    return <SignupCompletionGate><Navigate to="/onboarding" replace /></SignupCompletionGate>;
  }

  if (isOnboardingPage && onboardingCompleted) {
    return <SignupCompletionGate><Navigate to="/dashboard" replace /></SignupCompletionGate>;
  }

  return <SignupCompletionGate>{children}<NextSteps /></SignupCompletionGate>;
}
